/**
 * Shared Storage Redux Actions
 * Cross-cluster shared filesystem (mirrors storage.actions.js patterns).
 * Talks to /api/shared-storage. The Authorization header is set globally by
 * jwtService, so we only add optional org scoping here.
 */

import axios from 'axios';
import { showMessage } from 'app/store/actions/fuse';
import { API_BASE_URL } from '@config';

export const SHARED_STORAGE_LOADING = '[SHARED STORAGE] LOADING';
export const SHARED_STORAGE_SUCCESS = '[SHARED STORAGE] SUCCESS';
export const SHARED_STORAGE_ERROR = '[SHARED STORAGE] ERROR';
export const SHARED_STORAGE_SELECT = '[SHARED STORAGE] SELECT';
export const SHARED_STORAGE_CREATE_SUCCESS = '[SHARED STORAGE] CREATE SUCCESS';
export const SHARED_STORAGE_DELETE_SUCCESS = '[SHARED STORAGE] DELETE SUCCESS';
export const SHARED_STORAGE_FORM_OPEN = '[SHARED STORAGE] FORM OPEN';
export const SHARED_STORAGE_FORM_CLOSE = '[SHARED STORAGE] FORM CLOSE';

const getHeaders = (organizationId = null) => ({
	headers: {
		...(organizationId ? { 'x-organization-id': organizationId } : {})
	}
});

export function getSharedStorages(organizationId = null) {
	return (dispatch) => {
		dispatch({ type: SHARED_STORAGE_LOADING });
		return axios
			.get(`${API_BASE_URL}/api/shared-storage`, { ...getHeaders(organizationId), skipForbiddenToast: true })
			.then((response) => {
				dispatch({ type: SHARED_STORAGE_SUCCESS, payload: response.data });
			})
			.catch((error) => {
				dispatch({
					type: SHARED_STORAGE_ERROR,
					payload: error.response?.data?.error || 'Failed to load shared storage'
				});
				// 401/403 = viewer lacks storage permissions - render empty, no toast.
				const status = error.response?.status;
				if (status !== 401 && status !== 403) {
					dispatch(showMessage({ message: 'Failed to load shared storage', variant: 'error' }));
				}
			});
	};
}

export function createSharedStorage(data) {
	return (dispatch) => {
		return axios
			.post(`${API_BASE_URL}/api/shared-storage`, data)
			.then((response) => {
				dispatch({ type: SHARED_STORAGE_CREATE_SUCCESS, payload: response.data });
				dispatch(showMessage({ message: 'Shared storage created', variant: 'success' }));
				return response.data;
			})
			.catch((error) => {
				const errorMsg = error.response?.data?.error || 'Failed to create shared storage';
				dispatch(showMessage({ message: errorMsg, variant: 'error' }));
				throw error;
			});
	};
}

export function refreshSharedStorage(id) {
	return (dispatch) => {
		return axios
			.post(`${API_BASE_URL}/api/shared-storage/${id}/refresh`, {})
			.then((response) => {
				dispatch(getSharedStorages());
				return response.data;
			})
			.catch((error) => {
				const errorMsg = error.response?.data?.error || 'Failed to refresh shared storage';
				dispatch(showMessage({ message: errorMsg, variant: 'error' }));
				throw error;
			});
	};
}

export function attachSharedStorage(id, clusterId) {
	return (dispatch) => {
		return axios
			.post(`${API_BASE_URL}/api/shared-storage/${id}/attach`, { clusterId })
			.then((response) => {
				dispatch(showMessage({ message: 'Cluster attached to shared storage', variant: 'success' }));
				dispatch(getSharedStorages());
				return response.data;
			})
			.catch((error) => {
				const errorMsg = error.response?.data?.error || 'Failed to attach cluster';
				dispatch(showMessage({ message: errorMsg, variant: 'error' }));
				throw error;
			});
	};
}

export function detachSharedStorage(id, clusterId) {
	return (dispatch) => {
		return axios
			.post(`${API_BASE_URL}/api/shared-storage/${id}/detach`, { clusterId })
			.then((response) => {
				dispatch(showMessage({ message: 'Cluster detached from shared storage', variant: 'success' }));
				dispatch(getSharedStorages());
				return response.data;
			})
			.catch((error) => {
				const errorMsg = error.response?.data?.error || 'Failed to detach cluster';
				dispatch(showMessage({ message: errorMsg, variant: 'error' }));
				throw error;
			});
	};
}

export function deleteSharedStorage(id) {
	return (dispatch) => {
		return axios
			.delete(`${API_BASE_URL}/api/shared-storage/${id}`)
			.then(() => {
				dispatch({ type: SHARED_STORAGE_DELETE_SUCCESS, payload: id });
				dispatch(showMessage({ message: 'Shared storage deleted', variant: 'success' }));
			})
			.catch((error) => {
				const errorMsg = error.response?.data?.error || 'Failed to delete shared storage';
				dispatch(showMessage({ message: errorMsg, variant: 'error' }));
				throw error;
			});
	};
}

export function selectSharedStorage(item) {
	return { type: SHARED_STORAGE_SELECT, payload: item };
}

export function openSharedStorageForm() {
	return { type: SHARED_STORAGE_FORM_OPEN };
}

export function closeSharedStorageForm() {
	return { type: SHARED_STORAGE_FORM_CLOSE };
}
